import React, {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
} from 'react';
import clsx from 'clsx';
import { Icon } from 'ui-component-library/base';

interface CombiboxOption {
	value: string;
	label: string;
}

type CombiboxOptionInput = string | CombiboxOption;

interface CombiboxProps {
	id: string;
	name?: string;
	options: CombiboxOptionInput[];
	value?: string;
	onChange?: (event: {
		target: {
			value: string;
			name?: string;
			id: string;
		};
	}) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
	required?: boolean;
	ariaDescribedby?: string;
	ariaInvalid?: boolean;
	fieldRef?: React.RefCallback<HTMLInputElement>;
}

const normalizeOption = (option: CombiboxOptionInput): CombiboxOption => {
	if (typeof option === 'string') {
		return {
			value: option,
			label: option,
		};
	}

	return option;
};

const Combibox: React.FC<CombiboxProps> = ({
	id,
	name,
	options,
	value = '',
	onChange,
	onBlur,
	placeholder,
	className,
	disabled = false,
	required = false,
	ariaDescribedby,
	ariaInvalid = false,
	fieldRef,
}) => {
	const normalizedOptions = useMemo(
		() => options.map(normalizeOption),
		[options]
	);

	const selectedOption = useMemo(
		() => normalizedOptions.find((option) => option.value === value),
		[normalizedOptions, value]
	);

	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(selectedOption?.label || '');
	const [searchValue, setSearchValue] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputElementRef = useRef<HTMLInputElement | null>(null);
	const selectedLabelRef = useRef(selectedOption?.label || '');

	const setRefs = useCallback(
		(node: HTMLInputElement | null) => {
			inputElementRef.current = node;

			if (fieldRef) {
				fieldRef(node);
			}
		},
		[fieldRef]
	);

	useEffect(() => {
		const nextLabel = selectedOption?.label || '';
		setInputValue(nextLabel);
		selectedLabelRef.current = nextLabel;
	}, [selectedOption]);

	const filteredOptions = useMemo(() => {
		const searchText = searchValue.trim().toLowerCase();

		if (!searchText) {
			return normalizedOptions;
		}

		return normalizedOptions.filter((option) =>
			option.label.toLowerCase().includes(searchText)
		);
	}, [searchValue, normalizedOptions]);

	const emitChange = useCallback(
		(nextValue: string) => {
			if (!onChange) {
				return;
			}

			onChange({
				target: {
					value: nextValue,
					name,
					id,
				},
			});
		},
		[id, name, onChange]
	);

	const closeDropdown = useCallback((restoreSelectedLabel: boolean = false) => {
		setIsOpen(false);
		setHighlightedIndex(-1);
		setSearchValue('');

		if (restoreSelectedLabel) {
			setInputValue(selectedLabelRef.current);
		}
	}, []);

	const openDropdown = useCallback(() => {
		if (disabled) {
			return;
		}

		setIsOpen(true);
		setSearchValue('');
		setHighlightedIndex(
			selectedOption
				? normalizedOptions.findIndex(
						(option) => option.value === selectedOption.value
				  )
				: -1
		);
	}, [disabled, normalizedOptions, selectedOption]);

	const selectOption = useCallback(
		(option: CombiboxOption) => {
			setInputValue(option.label);
			closeDropdown();
			emitChange(option.value);
			inputElementRef.current?.focus();
		},
		[closeDropdown, emitChange]
	);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const nextValue = event.target.value;
		setInputValue(nextValue);
		setSearchValue(nextValue);

		if (!isOpen) {
			setIsOpen(true);
		}

		setHighlightedIndex(-1);

		if (!nextValue.trim()) {
			emitChange('');
		}
	};

	const handleInputClick = () => {
		if (!disabled) {
			openDropdown();
		}
	};

	const handleToggleClick = () => {
		if (disabled) {
			return;
		}

		if (isOpen) {
			closeDropdown();
		} else {
			openDropdown();
			inputElementRef.current?.focus();
		}
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const nextFocusedElement = event.relatedTarget as Node | null;

		if (
			nextFocusedElement &&
			containerRef.current?.contains(nextFocusedElement)
		) {
			return;
		}

		const matchedOption = normalizedOptions.find(
			(option) => option.label === inputValue
		);

		if (!matchedOption) {
			setInputValue(selectedLabelRef.current);
		}

		closeDropdown(true);

		if (onBlur) {
			onBlur(event);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (disabled) {
			return;
		}

		if (event.key === 'Escape') {
			if (isOpen) {
				event.preventDefault();
				closeDropdown(true);
			}
			inputElementRef.current?.blur();
			return;
		}

		if (
			!isOpen &&
			(event.key === 'ArrowDown' ||
				event.key === 'ArrowUp' ||
				event.key === 'Enter')
		) {
			event.preventDefault();
			openDropdown();
			return;
		}

		if (!isOpen) {
			return;
		}

		if (filteredOptions.length === 0) {
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
			}
			return;
		}

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				setHighlightedIndex((prev) => {
					if (prev < 0) {
						return 0;
					}

					return Math.min(prev + 1, filteredOptions.length - 1);
				});
				break;
			case 'ArrowUp':
				if (highlightedIndex <= 0) {
					closeDropdown(true);
					return;
				}

				event.preventDefault();
				setHighlightedIndex((prev) => Math.max(prev - 1, 0));
				break;
			case 'Enter':
				event.preventDefault();
				if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
					selectOption(filteredOptions[highlightedIndex]);
				}
				break;
			case 'Tab':
				closeDropdown(true);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		if (highlightedIndex >= 0 && dropdownRef.current) {
			const highlightedElement = dropdownRef.current.children[
				highlightedIndex
			] as HTMLElement | undefined;

			highlightedElement?.scrollIntoView({
				block: 'nearest',
			});
		}
	}, [highlightedIndex]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				closeDropdown(true);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [closeDropdown]);

	return (
		<div className="relative" ref={containerRef}>
			<input
				id={id}
				name={name}
				ref={setRefs}
				type="text"
				role="combobox"
				aria-autocomplete="list"
				aria-expanded={isOpen}
				aria-controls={`${id}-combibox-listbox`}
				aria-activedescendant={
					isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]
						? `${id}-combibox-option-${filteredOptions[highlightedIndex].value}`
						: undefined
				}
				aria-invalid={ariaInvalid}
				aria-describedby={ariaDescribedby}
				disabled={disabled}
				required={required}
				value={inputValue}
				placeholder={placeholder}
				autoComplete="off"
				onChange={handleInputChange}
				onClick={handleInputClick}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				className={clsx(
					className,
					'appearance-none',
					'text-p',
					'leading-5',
					'w-full',
					'pl-3 pr-8',
					'py-2',
					'border-2',
					!ariaInvalid && 'border-greySemiLight focus:border-blue',
					ariaInvalid && 'border-red',
					'rounded',
					'transition-all',
					'focus:outline-none',
					'disabled:text-greyLight',
					'disabled:cursor-not-allowed',
					'placeholder-greyDarker',
					'placeholder-opacity-100'
				)}
			/>

			<button
				type="button"
				tabIndex={-1}
				aria-label="Visa alternativ"
				onClick={handleToggleClick}
				disabled={disabled}
				className="absolute inset-y-0 right-0 flex items-center px-3 text-greyDarker focus:outline-none disabled:cursor-not-allowed"
			>
				<Icon
					icon="chevron"
					size={0.75}
					aria-hidden={true}
					className="ml-2"
					color="#8b8b8b"
					direction={isOpen ? 'down' : 'up'}
				/>
			</button>

			{isOpen && !disabled && (
				<div
					id={`${id}-combibox-listbox`}
					ref={dropdownRef}
					role="listbox"
					className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded border-2 border-greySemiLight bg-white shadow-lg"
				>
					{filteredOptions.length > 0 ? (
						filteredOptions.map((option, index) => (
							<button
								key={option.value}
								id={`${id}-combibox-option-${option.value}`}
								type="button"
								role="option"
								aria-selected={option.value === value}
								tabIndex={-1}
								onMouseDown={(event) => {
									event.preventDefault();
								}}
								onMouseEnter={() => setHighlightedIndex(index)}
								onClick={() => selectOption(option)}
								className={clsx(
									'block w-full px-3 py-2 text-left text-p leading-5 focus:outline-none',
									highlightedIndex === index && 'bg-greyLightest',
									option.value === value && 'bg-greyLightest font-bold'
								)}
							>
								{option.label}
							</button>
						))
					) : (
						<div className="px-3 py-2 text-p leading-5 text-greyDarker">
							Inga träffar
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Combibox;
